/**
 * Chart Color Utilities
 *
 * Generates color palettes for charts based on theme colors.
 * Ensures consistent, theme-aware visualization colors.
 */

import { ThemeColors } from '@/types/theme';

/**
 * Generates a color palette for charts based on theme colors
 *
 * @param themeColors - Theme color configuration
 * @param count - Number of colors needed (default: 12)
 * @returns Array of color hex codes
 *
 * Strategy:
 * - Uses primary, secondary, accent colors
 * - Generates variations (lighter/darker) for extended palette
 * - Ensures sufficient contrast and visual distinction
 */
export function generateChartColorPalette(
  themeColors: ThemeColors,
  count: number = 12
): string[] {
  const baseColors = [
    themeColors.primary,
    themeColors.secondary,
    themeColors.accent,
  ];

  // If we only need base colors, return them
  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }

  const palette: string[] = [...baseColors];

  // Generate variations of base colors to extend the palette
  const variations = [
    { lightness: 0.85, saturation: 0.6 }, // Lighter
    { lightness: 0.45, saturation: 1.1 }, // Darker
    { lightness: 0.65, saturation: 0.85 }, // Medium light
    { lightness: 0.35, saturation: 1.15 }, // Very dark
  ];

  // Generate color variations until we have enough colors
  for (const variation of variations) {
    if (palette.length >= count) break;

    for (const baseColor of baseColors) {
      if (palette.length >= count) break;

      const variedColor = varyColor(
        baseColor,
        variation.lightness,
        variation.saturation
      );
      palette.push(variedColor);
    }
  }

  return palette.slice(0, count);
}

/**
 * Generates a hover color variation
 *
 * @param color - Base color hex code
 * @param factor - Lightness adjustment factor (default: 0.85 = slightly darker)
 * @returns Hover color hex code
 */
export function generateHoverColor(color: string, factor: number = 0.85): string {
  return varyColor(color, factor, 1.0);
}

/**
 * Varies a color by adjusting lightness and saturation
 *
 * @param hex - Hex color code
 * @param lightnessMultiplier - Multiply lightness (1.0 = no change, >1 = lighter, <1 = darker)
 * @param saturationMultiplier - Multiply saturation (1.0 = no change, >1 = more saturated)
 * @returns New hex color code
 */
function varyColor(
  hex: string,
  lightnessMultiplier: number,
  saturationMultiplier: number
): string {
  // Convert hex to RGB
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  // Convert RGB to HSL
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Adjust lightness and saturation
  let newLightness = hsl.l * lightnessMultiplier;
  let newSaturation = hsl.s * saturationMultiplier;

  // Clamp values
  newLightness = Math.max(0, Math.min(1, newLightness));
  newSaturation = Math.max(0, Math.min(1, newSaturation));

  // Convert back to RGB
  const newRgb = hslToRgb(hsl.h, newSaturation, newLightness);

  // Convert RGB to hex
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Converts hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Converts RGB to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => {
    const hex = Math.round(x).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Converts RGB to HSL
 */
function rgbToHsl(
  r: number,
  g: number,
  b: number
): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h, s, l };
}

/**
 * Converts HSL to RGB
 */
function hslToRgb(
  h: number,
  s: number,
  l: number
): { r: number; g: number; b: number } {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: r * 255,
    g: g * 255,
    b: b * 255,
  };
}
